// Shared plumbing for Volunteer Tools (/volunteer): the session, the one fetch
// helper, the route link, and the stylesheet.
//
// ⚠️ THE SESSION LIVES IN localStorage, deliberately unlike the operator's
// (utils/adminSession.js keeps that in sessionStorage). A volunteer's only way
// back in is an email and a wait, so a session that died with the tab would
// cost them an inbox trip every time they closed the window; a volunteer's own
// laptop is the common case, and the server re-checks the account on every
// request, so a revoked volunteer holding a stored token is holding nothing.
// "Sign out" clears it.
//
// ⚠️ THE OPERATOR NEEDS NO VOLUNTEER LINK. /admin's launcher cards open these
// pages in the same tab, and the admin session (sessionStorage, per tab) is
// read FIRST: an admin token passes every volunteer gate server-side
// (`volunteerAccess` treats `tokenIsAdmin` as all three tools). It wins over a
// stored volunteer token because it only exists in a tab that came from /admin
// — the operator acting as the operator, not as whichever volunteer last used
// this browser. Requires ADMIN_PASSWORD to be configured: without it /admin
// signs in with a token that carries no `adm` claim, and the tools say so.

import { readAdminToken, writeAdminToken } from '../utils/adminSession.js';

export const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
export const TOKEN_KEY = 'umaps-volunteer-token';

export function readToken() {
  const admin = readAdminToken();
  if (admin) return admin;
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}
export function writeToken(t) {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch { /* private mode */ }
}
/**
 * `all` is the Sign out button: when the page was running on the operator's
 * session there is no separate thing to sign out of (see `clearToken` in
 * adminSession.js). A session the server merely REFUSED clears only the
 * volunteer token — refusing an admin token here must not sign the operator
 * out of /admin in the same tab.
 */
export function clearSession(all = true) {
  writeToken('');
  if (all) writeAdminToken('');
}

/**
 * The five tools, in the order the picker shows them. `id` is the scope the
 * server checks (`VOLUNTEER_SCOPES` in api/_accounts.js) and `path` is the
 * fixed URL /admin links to.
 */
export const TOOLS = [
  {
    id: 'moderate', path: '/volunteer/moderate', label: 'Content Moderator', icon: '🛡️',
    blurb: 'Approve or send back new club events, restaurant reviews and dish photos, place photos, and email a club about a problem.',
  },
  {
    id: 'issues', path: '/volunteer/issues', label: 'Issues & Reports', icon: '🧭',
    blurb: 'Work through problem reports and bad routes: open the route, note what is wrong, archive it once it is fixed.',
  },
  {
    id: 'content', path: '/volunteer/content', label: 'Content Filler', icon: '✏️',
    blurb: 'Fill in and correct study spots, libraries, microwaves and restaurants — hours, menus, photos, weekly highlights.',
  },
  {
    id: 'rooms', path: '/volunteer/rooms', label: 'Room Names', icon: '🚪',
    blurb: 'Give room numbers the names people actually use — "SSA Lounge" for Armes 209 — building by building, or search the whole campus.',
  },
  {
    id: 'routes', path: '/volunteer/routes', label: 'Route tester', icon: '🧪',
    blurb: 'Route between any two points on the campus map, compare the variants, try test edits to the graph, and suggest an improvement — nothing you do here changes the live map.',
  },
];

/**
 * One request. JSON in, JSON out, `{ ok, status, body }` back — never throws,
 * so every screen handles a network failure the same way. A 401 calls
 * `onExpired`, which sends the page back to the sign-in form.
 */
export async function api(path, { method = 'GET', body, token, onExpired } = {}) {
  try {
    const r = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const j = await r.json().catch(() => ({}));
    if (r.status === 401 && onExpired) onExpired(j.error);
    return { ok: r.ok && j.ok !== false, status: r.status, body: j };
  } catch {
    return { ok: false, status: 0, body: { error: 'Network error — check your connection and try again.' } };
  }
}

/** A File → a data URL, for the upload ops. */
export function readFileAsDataUrl(file) {
  return new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result || ''));
    fr.onerror = () => resolve('');
    fr.readAsDataURL(file);
  });
}

/**
 * The live map, reopened on the exact route an issue is about — From, To AND
 * both routing tri-states, because the same From→To with accessible on is a
 * different path. The same construction as /admin's `routeLink` (see the
 * CLAUDE.md "Route ratings triage" note); null when there is no room to route to.
 */
export function routeLink(issue) {
  const to = issue?.to;
  if (!to?.building || !to?.room) return null;
  try {
    const p = new URLSearchParams();
    if (issue.from) p.set('from', issue.from);
    p.set('b', to.building);
    p.set('r', to.room);
    const o = issue.options || {};
    if (o.accessible) p.set('a', '1');
    else if (o.noElevators) p.set('a', '2');
    if (o.tunnels) p.set('t', '1');
    else if (o.noTunnels) p.set('t', '2');
    return `${window.location.origin}/umanitoba?${p.toString()}`;
  } catch { return null; }
}

/** "3 min ago" / "Yesterday" / "Sep 14" — for a queue, recency is the fact. */
export function ago(ts) {
  const t = typeof ts === 'number' ? ts : Date.parse(ts || '');
  if (!Number.isFinite(t)) return '';
  const s = Math.round((Date.now() - t) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  if (s < 2 * 86400) return 'yesterday';
  if (s < 7 * 86400) return `${Math.round(s / 86400)} days ago`;
  return new Date(t).toLocaleDateString([], { month: 'short', day: 'numeric', year: s > 300 * 86400 ? 'numeric' : undefined });
}

export const CSS = `
.vt { --ink:#1b1e23; --dim:#5f6368; --faint:#8a8f98; --line:#e2e5ea; --page:#f6f7f9; --card:#fff;
  --accent:#1a73e8; --accentSoft:#e8f0fe; --good:#137333; --goodSoft:#e6f4ea; --bad:#c5221f; --badSoft:#fce8e6;
  --warn:#b06000; --warnSoft:#fef7e0;
  min-height:100vh; background:var(--page); color:var(--ink);
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif; font-size:14px; }
.vt *, .vt *::before, .vt *::after { box-sizing:border-box; }
/* Not on a link that IS a button: ".vt a" outranks ".vt-primary", which drew
   "Open route" as blue text on a blue button. */
.vt a:not(.vt-btn) { color:var(--accent); }
.vt-top { position:sticky; top:0; z-index:20; background:#fff; border-bottom:1px solid var(--line); }
.vt-topin { max-width:1180px; margin:0 auto; padding:10px 16px; display:flex; align-items:center; gap:14px; flex-wrap:wrap; }
.vt-nav { display:flex; gap:4px; flex-wrap:wrap; }
.vt-navbtn { border:none; background:none; padding:7px 11px; border-radius:8px; font:inherit; font-weight:650;
  color:var(--dim); cursor:pointer; display:inline-flex; gap:6px; align-items:center; }
.vt-navbtn:hover { background:var(--page); color:var(--ink); }
.vt-navbtn[aria-current="page"] { background:var(--accentSoft); color:var(--accent); }
.vt-who { margin-left:auto; display:flex; align-items:center; gap:10px; color:var(--dim); font-size:13px; }
.vt-main { max-width:1180px; margin:0 auto; padding:20px 16px 60px; }
.vt-h1 { font-size:22px; font-weight:800; margin:0 0 4px; letter-spacing:-.01em; }
.vt-sub { color:var(--dim); margin:0 0 18px; line-height:1.5; }
.vt-card { background:var(--card); border:1px solid var(--line); border-radius:12px; }
.vt-pad { padding:16px; }
.vt-btn { font:inherit; font-weight:650; border:1px solid var(--line); background:#fff; color:var(--ink);
  border-radius:8px; padding:7px 12px; cursor:pointer; white-space:nowrap; display:inline-flex; align-items:center; gap:6px;
  text-decoration:none; }
.vt-btn:hover:not(:disabled) { border-color:#c4c9d0; background:#fafbfc; }
.vt-btn:disabled { opacity:.5; cursor:default; }
.vt-primary { background:var(--accent); border-color:var(--accent); color:#fff; }
.vt-primary:hover:not(:disabled) { background:#1765cc; border-color:#1765cc; }
.vt-good { background:var(--good); border-color:var(--good); color:#fff; }
.vt-good:hover:not(:disabled) { background:#0f5c29; }
.vt-danger { color:var(--bad); border-color:#f3c5c1; }
.vt-danger:hover:not(:disabled) { background:var(--badSoft); }
.vt-dangerSolid { background:var(--bad); border-color:var(--bad); color:#fff; }
.vt-ghost { border-color:transparent; background:none; color:var(--dim); }
.vt-small { padding:4px 9px; font-size:12.5px; }
.vt-input, .vt-select, .vt-textarea { font:inherit; border:1px solid #d3d7dd; border-radius:8px; padding:8px 10px;
  background:#fff; color:var(--ink); width:100%; }
.vt-input:focus, .vt-select:focus, .vt-textarea:focus { outline:2px solid var(--accentSoft); border-color:var(--accent); }
.vt-textarea { resize:vertical; min-height:72px; line-height:1.45; }
.vt-label { display:block; font-size:12px; font-weight:700; color:var(--dim); margin:0 0 5px; text-transform:uppercase; letter-spacing:.04em; }
.vt-field { margin-bottom:14px; }
.vt-hint { font-size:12px; color:var(--faint); margin-top:4px; line-height:1.4; }
.vt-tabs { display:flex; gap:6px; flex-wrap:wrap; margin:0 0 14px; }
.vt-tab { font:inherit; font-weight:650; border:1px solid var(--line); background:#fff; border-radius:999px;
  padding:6px 13px; cursor:pointer; color:var(--dim); display:inline-flex; gap:7px; align-items:center; }
.vt-tab[aria-pressed="true"] { background:var(--ink); color:#fff; border-color:var(--ink); }
.vt-count { font-size:11.5px; font-weight:800; background:rgba(0,0,0,.07); border-radius:999px; padding:1px 7px; }
.vt-tab[aria-pressed="true"] .vt-count { background:rgba(255,255,255,.2); }
.vt-count-hot { background:var(--bad) !important; color:#fff; }
.vt-toolbar { display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:12px; }
.vt-toolbar .vt-input { max-width:320px; }
.vt-pill { display:inline-flex; align-items:center; gap:4px; font-size:11px; font-weight:800; letter-spacing:.03em;
  text-transform:uppercase; border-radius:999px; padding:2px 8px; white-space:nowrap; }
.vt-pill-new { background:var(--accentSoft); color:var(--accent); }
.vt-pill-warn { background:var(--warnSoft); color:var(--warn); }
.vt-pill-good { background:var(--goodSoft); color:var(--good); }
.vt-pill-bad { background:var(--badSoft); color:var(--bad); }
.vt-pill-dim { background:#eceff1; color:#3c4043; }
.vt-list { display:flex; flex-direction:column; gap:10px; }
.vt-row { background:#fff; border:1px solid var(--line); border-radius:12px; padding:12px 14px; }
.vt-row-hl { border-color:#b7cff8; box-shadow:0 0 0 3px var(--accentSoft); }
.vt-rowhead { display:flex; gap:12px; align-items:flex-start; }
.vt-logo { width:44px; height:44px; border-radius:10px; object-fit:contain; background:#f1f3f4; flex:none; }
.vt-logoNone { width:44px; height:44px; border-radius:10px; background:#f1f3f4; flex:none; display:flex;
  align-items:center; justify-content:center; font-size:20px; }
.vt-grow { flex:1; min-width:0; }
.vt-title { font-weight:750; font-size:15px; line-height:1.3; }
.vt-meta { color:var(--dim); font-size:13px; line-height:1.45; margin-top:2px; }
.vt-actions { display:flex; gap:6px; flex-wrap:wrap; align-items:center; justify-content:flex-end; }
.vt-empty { text-align:center; color:var(--dim); padding:40px 16px; background:#fff; border:1px dashed var(--line); border-radius:12px; }
.vt-err { background:var(--badSoft); color:var(--bad); border-radius:8px; padding:9px 12px; margin-bottom:12px; }
.vt-ok { background:var(--goodSoft); color:var(--good); border-radius:8px; padding:9px 12px; margin-bottom:12px; }
.vt-note { background:var(--warnSoft); color:#7a4a00; border-radius:8px; padding:9px 12px; font-size:13px; line-height:1.45; }
.vt-seg { display:inline-flex; border:1px solid var(--line); border-radius:8px; overflow:hidden; }
.vt-seg button { font:inherit; font-size:12.5px; font-weight:650; border:none; border-right:1px solid var(--line);
  background:#fff; padding:5px 10px; cursor:pointer; color:var(--dim); }
.vt-seg button:last-child { border-right:none; }
.vt-seg button[aria-pressed="true"] { background:var(--ink); color:#fff; }
.vt-seg button:disabled { cursor:default; }
.vt-scrim { position:fixed; inset:0; background:rgba(20,24,31,.45); z-index:50; display:flex;
  align-items:flex-start; justify-content:center; padding:5vh 12px; overflow-y:auto; }
.vt-modal { background:#fff; border-radius:14px; width:100%; max-width:680px; box-shadow:0 20px 60px rgba(0,0,0,.25); }
.vt-modalhead { display:flex; gap:12px; align-items:flex-start; padding:16px 18px; border-bottom:1px solid var(--line); }
.vt-modalbody { padding:16px 18px; }
.vt-modalfoot { display:flex; gap:8px; justify-content:flex-end; flex-wrap:wrap; padding:12px 18px; border-top:1px solid var(--line); background:#fafbfc; border-radius:0 0 14px 14px; }
.vt-facts { display:grid; grid-template-columns:120px 1fr; gap:6px 12px; font-size:13.5px; margin:0 0 14px; }
.vt-facts dt { color:var(--dim); font-weight:650; }
.vt-facts dd { margin:0; min-width:0; overflow-wrap:anywhere; }
.vt-log { list-style:none; margin:0; padding:0; font-size:12.5px; color:var(--dim); }
.vt-log li { padding:4px 0; border-top:1px solid #f0f1f3; }
.vt-chips { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:8px; }
.vt-chip { font:inherit; font-size:12.5px; border:1px solid var(--line); background:#fff; border-radius:999px; padding:4px 10px; cursor:pointer; color:var(--dim); }
.vt-chip:hover { border-color:var(--accent); color:var(--accent); }
.vt-picker { display:grid; grid-template-columns:repeat(auto-fit,minmax(250px,1fr)); gap:14px; }
.vt-tool { text-align:left; font:inherit; background:#fff; border:1px solid var(--line); border-radius:14px; padding:18px;
  cursor:pointer; display:flex; flex-direction:column; gap:8px; color:var(--ink); min-height:170px; }
.vt-tool:hover:not(:disabled) { border-color:var(--accent); box-shadow:0 4px 16px rgba(26,115,232,.12); }
.vt-tool:disabled { cursor:default; opacity:.55; }
.vt-toolicon { font-size:26px; }
.vt-toolname { font-weight:800; font-size:16px; }
.vt-toolblurb { color:var(--dim); line-height:1.5; }
.vt-signin { max-width:420px; margin:9vh auto 0; }
.vt-split { display:grid; grid-template-columns:320px 1fr; gap:16px; align-items:start; }
.vt-side { position:sticky; top:70px; max-height:calc(100vh - 90px); display:flex; flex-direction:column; }
.vt-sidelist { overflow-y:auto; border-top:1px solid var(--line); }
.vt-sideitem { display:block; width:100%; text-align:left; font:inherit; background:#fff; border:none;
  border-bottom:1px solid #f0f1f3; padding:9px 12px; cursor:pointer; color:var(--ink); }
.vt-sideitem:hover { background:#fafbfc; }
.vt-sideitem[aria-current="true"] { background:var(--accentSoft); }
.vt-photos { display:grid; grid-template-columns:repeat(auto-fill,minmax(110px,1fr)); gap:8px; }
.vt-photo { position:relative; border-radius:9px; overflow:hidden; background:#f1f3f4; aspect-ratio:1/1; }
.vt-photo img { width:100%; height:100%; object-fit:cover; display:block; }
.vt-photo-hidden img { opacity:.28; filter:grayscale(1); }
.vt-photobar { position:absolute; left:0; right:0; bottom:0; display:flex; justify-content:space-between; gap:4px;
  padding:4px; background:linear-gradient(transparent,rgba(0,0,0,.55)); }
.vt-photobar span { color:#fff; font-size:10.5px; font-weight:700; align-self:flex-end; }
.vt-photobar button { font:inherit; font-size:11px; font-weight:700; border:none; border-radius:6px; padding:3px 7px; cursor:pointer; background:rgba(255,255,255,.92); }
.vt-hours { display:grid; grid-template-columns:52px 1fr 1fr auto; gap:6px 8px; align-items:center; }
.vt-menu { width:100%; border-collapse:collapse; font-size:13px; }
.vt-menu th { text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:.04em; color:var(--dim); padding:6px; border-bottom:1px solid var(--line); }
.vt-menu td { padding:5px 6px; border-bottom:1px solid #f0f1f3; vertical-align:top; }
.vt-menu tr.vt-changed td { background:#fffbea; }
.vt-menu tr.vt-removed td { opacity:.45; text-decoration:line-through; }
.vt-menu .vt-input { padding:5px 7px; font-size:13px; }
.vt-changedmark { font-size:11px; font-weight:800; color:var(--warn); }
.vt-savebar { position:sticky; bottom:0; margin-top:16px; background:#fff; border:1px solid var(--line); border-radius:12px;
  padding:10px 14px; display:flex; gap:8px; align-items:center; justify-content:flex-end; box-shadow:0 -6px 20px rgba(0,0,0,.05); }
.vt-savebar .vt-grow { color:var(--dim); font-size:13px; }
.vt-issue-msg { white-space:pre-wrap; overflow-wrap:anywhere; line-height:1.45; }
.vt-clamp { display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.vt-route { font-weight:650; overflow-wrap:anywhere; }
.vt-devnote { background:var(--goodSoft); border-radius:8px; padding:9px 12px; font-size:13px; margin-top:10px; line-height:1.45; }
.vt-rowbody { margin-top:12px; padding-top:12px; border-top:1px solid #f0f1f3; }
/* Room Names: one row per room, the name box IS the row. */
.vt-rooms { width:100%; border-collapse:collapse; font-size:13.5px; }
.vt-rooms th { text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:.04em; color:var(--dim); padding:6px 8px; border-bottom:1px solid var(--line); }
.vt-rooms td { padding:4px 8px; border-bottom:1px solid #f0f1f3; vertical-align:middle; }
.vt-rooms tr.vt-draft td { background:#fffbea; }
.vt-rooms .vt-input { padding:6px 8px; font-size:13.5px; }
.vt-roomno { font-weight:750; white-space:nowrap; font-variant-numeric:tabular-nums; width:1%; }
.vt-roomstate { white-space:nowrap; width:1%; }
.vt-roomwas { font-size:12px; color:var(--faint); }
.vt-results { border:1px solid var(--line); border-radius:12px; background:#fff; margin:-6px 0 14px; max-height:320px; overflow-y:auto; }
.vt-stats { display:flex; gap:18px; flex-wrap:wrap; font-size:13px; color:var(--dim); }
.vt-stats b { color:var(--ink); font-size:16px; display:block; }
/* Route tester: the map is full-bleed under the header, the page itself does
   not scroll (the tool's own panel does). Set by VolunteerPage on that tool. */
.vt.vt-bleed { display:flex; flex-direction:column; height:100vh; overflow:hidden; }
.vt-bleed .vt-main { flex:1; min-height:0; max-width:none; width:100%; padding:0; display:flex; flex-direction:column; }
.vt-bleed .vt-main > * { flex:1; min-height:0; }
@media (max-width: 820px) {
  .vt.vt-bleed { height:auto; overflow:visible; }
  .vt-split { grid-template-columns:1fr; }
  .vt-side { position:static; max-height:none; }
  .vt-sidelist { max-height:260px; }
  .vt-rowhead { flex-wrap:wrap; }
  .vt-actions { justify-content:flex-start; width:100%; }
  .vt-facts { grid-template-columns:1fr; }
  .vt-who { margin-left:0; }
}
`;
